import { useAuth0 } from "@auth0/auth0-react";
import React, { useEffect } from "react";
import { isSessionExpiredError } from "../lib/authErrors";
import { useOverlayControl } from "./AppOverlayController";

// Token-driven: detection lag is bounded by the access-token TTL, not Auth0's session lifetime.
export const CHECK_INTERVAL_MS = 60_000;

// Proactively detects session expiry so an idle tab (no GraphQL traffic) is still returned to login.
export const SessionExpiryWatcher: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { activeOverlay, reportSessionExpired } = useOverlayControl();
  const sessionExpired = activeOverlay === "session_expired";

  useEffect(() => {
    // Nothing to watch once expiry is known — the app is tearing down.
    if (sessionExpired) {
      return undefined;
    }

    let cancelled = false;

    const check = async (): Promise<void> => {
      try {
        await getAccessTokenSilently();
      } catch (err) {
        // Non-expiry failures (network) are owned by the heartbeat / Apollo error link.
        if (!cancelled && isSessionExpiredError(err)) {
          reportSessionExpired();
        }
      }
    };

    // Mount check — a tab reloaded after a long idle shouldn't wait a full interval.
    void check();

    const intervalId = setInterval((): void => {
      void check();
    }, CHECK_INTERVAL_MS);

    // Check on tab focus — catches "left it open, came back" without waiting a full interval.
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") {
        void check();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return (): void => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [sessionExpired, getAccessTokenSilently, reportSessionExpired]);

  return null;
};
